import { endOfDay } from 'date-fns';
import type { Pagination } from 'models';
import { useInfiniteQuery } from 'query';
import type { PaginationData } from 'common/services';
import { getNodeTodos } from '../../services';
import { type NodeTodo, type NodeTodoType, createNodeTodo } from '../../types';
import { getNodeTodosInfiniteQueryKey } from './keys';

export type NodeTodosQueryParams = {
  filter: { endTime?: Date | null; starTime?: Date | null; types?: NodeTodoType[]; userId: string };
  pagination: Omit<Pagination, 'position'>;
};

export const useNodeTodosQuery = ({ filter, pagination }: NodeTodosQueryParams) =>
  useInfiniteQuery<PaginationData<NodeTodo>, number>({
    gcTime: 0,
    async queryFn({ pageParam }) {
      const { data: todos } = await getNodeTodos({
        config: {
          params: {
            $limit: pagination.pageSize,
            $or: [
              filter.endTime === null
                ? {}
                : { endTime: { $gte: filter.endTime?.toISOString() ?? new Date().toISOString() } },
            ],
            $skip: pageParam,
            $sort: { sortAt: '1' },
            assignee: filter.userId,
            startTime:
              filter.starTime === null
                ? undefined
                : { $lte: filter.starTime?.toISOString() ?? endOfDay(new Date()).toISOString() },
            status: 'active',
            type: filter.types ? { $in: filter.types } : undefined,
          },
        },
      });

      return {
        data: todos.data.map((todoDataObject) => createNodeTodo(todoDataObject)),
        limit: todos.limit,
        skip: todos.skip,
        total: todos.total,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) =>
      lastPage.skip + lastPage.data.length < lastPage.total ? lastPage.skip + lastPage.limit : undefined,
    queryKey: getNodeTodosInfiniteQueryKey({ filter, pagination }),
    refetchInterval: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });
